import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { isEmpty } from 'lodash'
import { DownloadRounded, RestartAltRounded, NavigateNext } from '@mui/icons-material'
import { Box, Breadcrumbs, Button, Link, Stack, Typography, useTheme } from '@mui/material'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { Module as CareerModule, ModuleConfig } from '../../shared/model'
import { ContextModel, FsOFAValue, Module } from '../../shared/model/models'
import { diversityDimensionMap, moduleConfigMap } from '../../shared/constants'
import {
  changeOFAValues,
  setContextVariantGapLatest,
  setCWIMappings,
  setOFAOverall,
  setOFATrainingCost,
  setOFAValues,
} from '../../store/actions'
import { downloadOFAGuide } from '../../components/fs-options-for-actions/fs-ofa-guide'
import FsOFATable, { OFATableRef } from '../../components/fs-options-for-actions/fs-ofa-table'
import FsOFAInfoCards from '../../components/fs-options-for-actions/fs-ofa-info-cards'
import { useLoading } from '../../shared/hooks'
import ACTIONS from '../../store/constants'
import FsAlert, { FsAlertRef } from '../../components/common/atoms/Alert'
import FsOFACareer from '../../components/fs-options-for-actions/fs-ofa-career'
import { translatedDomainName } from '../../shared/utils'
import FsOFATextField from '../../components/fs-options-for-actions/fs-ofa-textfield'
import { getIcon } from '../../components/common/Icons'
import FsFullPageLoader from '../../components/common/atoms/FullPageLoader'

type NavigationParams = {
  id?: string
}

const FsOFA = () => {
  const { t, i18n } = useTranslation()
  const { id } = useParams<NavigationParams>()
  const contextModelVariantId = parseInt(id!, 10)
  const [moduleConfig, setModuleConfig] = useState<ModuleConfig>()
  const [contextModels, setContextModels] = useState<ContextModel[]>([])
  const {
    modules,
    currentRun,
    ofaValues,
    ofaBEValues,
    ofaOverall,
    ofaTrainingCost,
    contextVariantGapLatest,
    cwiMappings,
  } = useAppSelector((state) => state.currentAnalysis)
  const [downloading, setDownloading] = useState<boolean>(false)
  const dispatch = useAppDispatch()
  const theme = useTheme()
  const navigate = useNavigate()
  const [showInfoCards, setShowInfoCards] = useState<boolean>(true)
  const [shouldRecalculate, setShouldRecalculate] = useState<boolean>(false)

  const ref = useRef<OFATableRef>(null)
  const alertRef = useRef<FsAlertRef>(null)

  useEffect(() => {
    if (currentRun && (!cwiMappings || !cwiMappings[currentRun.id])) {
      dispatch(setCWIMappings({ runId: currentRun.id }))
    }
  }, [currentRun])

  useEffect(() => {
    if (currentRun) {
      const cmvId = parseInt(id!, 10)
      if (!contextVariantGapLatest || isEmpty(contextVariantGapLatest[cmvId])) {
        dispatch(setContextVariantGapLatest({ contextVariantId: cmvId, date: currentRun.runAt }))
      }
    }
  }, [id, contextVariantGapLatest, currentRun])

  const findCurrentModule = (odules: Module[]) =>
    odules.find((module) =>
      module.contextModels.some((cm) =>
        cm.contextModelVariants.some((cmv) => cmv.id === contextModelVariantId),
      ),
    )

  const findCurrentContextModel = () =>
    contextModels?.find((cm) =>
      cm.contextModelVariants.some((cmv) => cmv.id === contextModelVariantId),
    )

  useEffect(() => {
    if (modules) {
      const currentModule = findCurrentModule(modules)
      if (currentModule) {
        setModuleConfig(moduleConfigMap.get(currentModule.name))
        setContextModels(currentModule.contextModels)
      }
    }
  }, [id, modules])

  const curDivDimName = findCurrentContextModel()?.diversityDimension.name ?? ''
  const diversityDimenstionConfig = diversityDimensionMap.get(curDivDimName)

  useEffect(() => {
    if (currentRun && moduleConfig?.module && (!ofaValues || !ofaValues[contextModelVariantId])) {
      dispatch(
        setOFAValues({
          contextModelVariantId,
          runId: currentRun.id,
          module: moduleConfig?.module ?? '',
        }),
      )
    }
  }, [contextModelVariantId, currentRun, moduleConfig])

  const { isFetching } = useLoading([
    ACTIONS.ANALYSIS.SET_CONTEXT_VARIANT_GAP_LATEST,
    ACTIONS.ANALYSIS.SET_OFA_VALUES,
    ACTIONS.ANALYSIS.SET_OFA_OVERALL,
    ACTIONS.ANALYSIS.SET_CWI_MAPPINGS,
  ])

  useEffect(() => {
    if (
      currentRun &&
      ofaValues &&
      ofaValues[contextModelVariantId] &&
      (!ofaOverall || !ofaOverall[contextModelVariantId])
    ) {
      dispatch(setOFAOverall({ contextModelVariantId }))
    }
  }, [contextModelVariantId, currentRun, ofaValues])

  const saveAction = async () => {
    if (currentRun) {
      setDownloading(true)
      setShowInfoCards(false)
      await ref.current?.saveActions()
      await dispatch(
        setOFAValues({
          contextModelVariantId,
          runId: currentRun.id,
          module: moduleConfig?.module ?? '',
        }),
      )
      setShowInfoCards(true)
      setShouldRecalculate(false)
      setDownloading(false)
    }
  }

  const downloadGuide = async (level?: string) => {
    if (
      currentRun &&
      ofaValues &&
      ofaValues[contextModelVariantId] &&
      ofaOverall &&
      ofaOverall[contextModelVariantId] &&
      contextVariantGapLatest &&
      contextVariantGapLatest[contextModelVariantId] &&
      cwiMappings &&
      cwiMappings[currentRun.id] &&
      (moduleConfig?.module !== CareerModule.TRAINING || !!ofaTrainingCost)
    ) {
      try {
        setDownloading(true)
        if (shouldRecalculate) {
          alertRef.current?.openAlert({
            severity: 'error',
            message: t('ofa.save.actions'),
          })
          return
        }
        await downloadOFAGuide(
          moduleConfig?.module === CareerModule.CAREER
            ? ofaValues[contextModelVariantId]
                .filter((v) => v.isCurrentOption)
                .sort((a, b) => (a.careerLevel ?? 0) - (b.careerLevel ?? 0))
            : ofaValues[contextModelVariantId]
                .filter((v) => v.isCurrentOption)
                .sort(
                  (a, b) => parseInt(a.ofaVariableLabel, 10) - parseInt(b.ofaVariableLabel, 10),
                ),
          ofaOverall[contextModelVariantId],
          contextVariantGapLatest[contextModelVariantId].description.groupOfInterest.groupVariable
            .label,
          contextVariantGapLatest[contextModelVariantId].description.referenceGroup.groupVariable
            .label,
          t,
          i18n,
          currentRun.runAt,
          cwiMappings[currentRun.id],
          moduleConfig,
          diversityDimenstionConfig,
          moduleConfig?.module === CareerModule.TRAINING ? ofaTrainingCost : undefined,
        )
      } catch {
        alertRef.current?.openAlert({ severity: 'error', message: t('something.went.wrong') })
      } finally {
        setDownloading(false)
      }
    }
  }

  const resetAction = async () => {
    if (currentRun) {
      setDownloading(true)
      setShowInfoCards(false)
      await ref.current?.resetActions()
      await dispatch(
        setOFAValues({
          contextModelVariantId,
          runId: currentRun.id,
          module: moduleConfig?.module ?? '',
        }),
      )
      setShowInfoCards(true)
      setShouldRecalculate(false)
      setDownloading(false)
    }
  }

  useEffect(() => {
    setDownloading(isFetching)
  }, [isFetching])

  const goiLabel =
    contextVariantGapLatest && contextVariantGapLatest[contextModelVariantId]
      ? translatedDomainName(
          contextVariantGapLatest[contextModelVariantId].description.groupOfInterest.groupVariable
            .label,
          t,
        )
      : ''
  const rgLabel =
    contextVariantGapLatest && contextVariantGapLatest[contextModelVariantId]
      ? translatedDomainName(
          contextVariantGapLatest[contextModelVariantId].description.referenceGroup.groupVariable
            .label,
          t,
        )
      : ''

  const referenceGroupCount =
    contextVariantGapLatest && contextVariantGapLatest[contextModelVariantId]
      ? Math.round(
          contextVariantGapLatest[contextModelVariantId].description.referenceGroup
            .descriptionDependentVariable.numberOfObservations,
        )
      : 0
  const groupOfInterestCount =
    contextVariantGapLatest && contextVariantGapLatest[contextModelVariantId]
      ? Math.round(
          contextVariantGapLatest[contextModelVariantId].description.groupOfInterest
            .descriptionDependentVariable.numberOfObservations,
        )
      : 0

  const groupOfInterestPercentage =
    contextVariantGapLatest && contextVariantGapLatest[contextModelVariantId]
      ? (groupOfInterestCount * 100) / (referenceGroupCount + groupOfInterestCount)
      : 0

  useEffect(() => {
    if (
      ofaBEValues &&
      ofaBEValues[contextModelVariantId] &&
      moduleConfig?.module === CareerModule.TRAINING &&
      ofaTrainingCost
    ) {
      dispatch(
        changeOFAValues(
          contextModelVariantId,
          ofaBEValues[contextModelVariantId].map((rowData: FsOFAValue) => ({
            ...rowData,
            additionalCost:
              rowData.additionalCost !== null
                ? `${Number(Number(rowData.additionalCost) * ofaTrainingCost).toFixed(2)}`
                : null,
            costAsPercentageOfPayroll:
              rowData.additionalCost !== null
                ? `${Number(
                    (Number(rowData.additionalCost) * ofaTrainingCost * 100) /
                      Number(rowData.costAsPercentageOfPayroll),
                  ).toFixed(3)}`
                : null,
          })),
        ),
      )
    }
  }, [ofaTrainingCost, moduleConfig])

  return (
    <div>
      <Box>
        <Stack direction="row" alignItems="center">
          <Box mr={1} mt={0.75}>
            {getIcon(moduleConfig?.module ?? '', {
              color: moduleConfig?.fsColor[theme.palette.mode === 'light' ? 'primary' : 'light'],
            })}
          </Box>
          {getIcon(diversityDimenstionConfig?.diversityDimension ?? '', {
            color:
              diversityDimenstionConfig?.fsColor[
                theme.palette.mode === 'light' ? 'primary' : 'light'
              ],
          })}
          <Typography variant="h1" ml={1} component="div" className="flex items-end">
            {t(`module.name.${moduleConfig?.module}` as any)} {t('by')}{' '}
            {t(`context.model.name.${curDivDimName}` as any)}
            <Typography variant="h3" ml={1}>
              ({goiLabel} {t('vs.')} {rgLabel})
            </Typography>
          </Typography>
        </Stack>
      </Box>
      <Stack
        mt={0.5}
        mb={2}
        direction={{ sm: 'column', md: 'row' }}
        alignItems={{ sm: 'flex-start', md: 'center' }}
        justifyContent={{ sm: 'center', md: 'space-between' }}
      >
        <Stack direction="row" alignItems="center" mt={1}>
          <Breadcrumbs separator={<NavigateNext fontSize="small" />} aria-label="breadcrumb">
            <Link
              underline="hover"
              key="1"
              color="text.secondary"
              sx={{ cursor: 'pointer' }}
              onClick={() => navigate('/fs/dashboard')}
            >
              <Typography variant="body2">{t('overview')}</Typography>
            </Link>
            <Link
              underline="hover"
              key="2"
              color="text.secondary"
              sx={{ cursor: 'pointer' }}
              onClick={() => {
                navigate(`/fs/fair-${moduleConfig?.module}/${id}`)
              }}
            >
              <Typography variant="body2">
                {t(`module.name.${moduleConfig?.module}` as any)} {t('by')}{' '}
                {t(`context.model.name.${curDivDimName}` as any)}
              </Typography>
            </Link>
            <Typography color="text.primary" variant="body2" className="font-semibold">
              {t('options.for.action')}
            </Typography>
          </Breadcrumbs>
        </Stack>
        <Stack direction="row" alignItems="center">
          <Button startIcon={<RestartAltRounded />} variant="outlined" onClick={resetAction}>
            {t('ofa.reset')}
          </Button>
          <Button
            variant="contained"
            sx={{ ml: 2 }}
            onClick={() => downloadGuide()}
            startIcon={<DownloadRounded />}
          >
            {t('ofa.download.guide.button')}
          </Button>
        </Stack>
      </Stack>

      {moduleConfig?.module === CareerModule.TRAINING && (
        <Stack mb={2} direction="row" alignItems="center">
          <Typography>{t('ofa.yearly.training.cost')}</Typography>
          <FsOFATextField
            unit={`\u20ac/${t('year')}`}
            value={ofaTrainingCost}
            onChange={(cost: number) => {
              if (cost) {
                dispatch(setOFATrainingCost(cost))
                setShouldRecalculate(true)
              }
            }}
          />
        </Stack>
      )}
      <FsOFAInfoCards
        moduleConfig={moduleConfig}
        diversityDimenstionConfig={diversityDimenstionConfig}
        showInfoCards={showInfoCards}
        shouldRecalculate={shouldRecalculate}
        saveAction={saveAction}
        values={
          ofaOverall && ofaOverall[contextModelVariantId] ? ofaOverall[contextModelVariantId] : null
        }
      />
      {ofaValues && ofaValues[contextModelVariantId] && moduleConfig ? (
        moduleConfig.module === CareerModule.CAREER ? (
          <FsOFACareer
            moduleConfig={moduleConfig}
            diversityDimenstionConfig={diversityDimenstionConfig}
            ref={ref}
            setShouldRecalculate={setShouldRecalculate}
            rows={ofaValues[contextModelVariantId]}
            contextModelVariantId={contextModelVariantId}
            goiLabel={goiLabel}
          />
        ) : (
          <FsOFATable
            moduleConfig={moduleConfig}
            diversityDimenstionConfig={diversityDimenstionConfig}
            ref={ref}
            setShouldRecalculate={setShouldRecalculate}
            rows={ofaValues[contextModelVariantId]}
            contextModelVariantId={contextModelVariantId}
            goiLabel={goiLabel}
          />
        )
      ) : null}
      {moduleConfig?.module === CareerModule.CAREER && (
        <Typography variant="body2" component="div" style={{ whiteSpace: 'pre-line' }}>
          *{' '}
          {i18n.language === 'de'
            ? `Empfohlener (neuer) Gap = Dieser Wert ist nicht Null, sondern genau so hoch, dass damit, bei Erreichen dieses Gap, der Anteil der benachteiligten Gruppe im Unternehmen (hier: ${goiLabel}) in der entsprechenden Karrierestufe entsprechend repräsentiert ist.\nUm beispielsweise in einer Karrierestufe den Anteil der ${goiLabel} entsprechend deren Anteil im Unternehmen (${groupOfInterestPercentage.toLocaleString(
                'de',
                { minimumFractionDigits: 2, maximumFractionDigits: 2 },
              )} %)zu erreichen, sollte der Gap in jeder Karrierestufe idealerweise bei ${
                ofaOverall && ofaOverall[contextModelVariantId]
                  ? ofaOverall[contextModelVariantId].overallGap.toLocaleString('de', {
                      maximumFractionDigits: 1,
                    })
                  : ''
              } % liegen.`
            : `Recommended (new) gap = This value is not zero, but exactly as high as necessary to represent the share of the disadvantaged group in the company (here: ${goiLabel}) at the corresponding career level when this gap is reached.\nFor example, in order to achieve the share of the ${goiLabel} in a career level corresponding to their share in the company (${groupOfInterestPercentage.toLocaleString(
                'en',
                { minimumFractionDigits: 2, maximumFractionDigits: 2 },
              )} %), the gap in each career level should ideally be ${
                ofaOverall && ofaOverall[contextModelVariantId]
                  ? ofaOverall[contextModelVariantId].overallGap.toLocaleString('en', {
                      maximumFractionDigits: 1,
                    })
                  : ''
              } %.`}
        </Typography>
      )}
      {downloading && <FsFullPageLoader />}
      <FsAlert ref={alertRef} />
    </div>
  )
}

export default FsOFA
